// frontend/src/components/safety/MakeItRealButton.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid
} from '@heroicons/react/24/solid';

const MakeItRealButton = ({
  onConfirm,
  simulationResult,
  className = '',
  disabled = false
}) => {
  const [showModal, setShowModal] = useState(false);
  const [checklist, setChecklist] = useState([
    { id: 'taxes', label: 'I understand the tax implications of this strategy', checked: false },
    { id: 'risk', label: 'I understand the risks involved', checked: false },
    { id: 'reviewed', label: 'I have reviewed my portfolio and current allocation', checked: false },
    { id: 'afford', label: 'I can afford this change without impacting my emergency fund', checked: false },
    { id: 'goal', label: 'This aligns with my financial goals', checked: false }
  ]);

  const allChecked = checklist.every(item => item.checked);

  const handleChecklistChange = (id) => {
    setChecklist(prev => 
      prev.map(item => 
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const handleConfirm = () => {
    if (allChecked) {
      onConfirm();
      setShowModal(false);
      setChecklist(prev => prev.map(item => ({ ...item, checked: false })));
    }
  };

  return (
    <>
      <motion.button
        onClick={() => !disabled && setShowModal(true)}
        disabled={disabled}
        className={`btn-primary relative w-full py-4 px-6 text-lg font-semibold ${className}`}
      >
        <div className="flex items-center justify-center gap-3">
          <ShieldCheckIcon className="w-6 h-6" />
          <span>Make it Real</span>
          <ExclamationTriangleIcon className="w-5 h-5 opacity-70" />
        </div>
      </motion.button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              onClick={(e) => e.stopPropagation()}
              className="surface-panel max-w-lg w-full p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pine-tint rounded-lg">
                    <ShieldCheckIcon className="w-6 h-6 text-pine" />
                  </div>
                  <div>
                    <h2 className="text-xl font-display font-semibold text-primary">Safety Checklist</h2>
                    <p className="text-sm text-secondary">Please confirm before proceeding</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-ghost p-2"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6 p-4 border border-warning/40 bg-pine-tint rounded-lg">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-warning mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-secondary">
                    <p className="font-semibold mb-1 text-primary">You're about to execute real trades</p>
                    <p>This will make actual changes to your portfolio. Please review carefully.</p>
                  </div>
                </div>
              </div>

              {simulationResult && (
                <div className="mb-6 p-4 border border-hairline bg-bone rounded-lg">
                  <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                    <CurrencyDollarIcon className="w-4 h-4 text-pine" />
                    Simulation Result
                  </h3>
                  <div className="space-y-1 text-sm text-secondary">
                    {simulationResult.portfolio_delta?.value_change !== undefined && (
                      <div>
                        Portfolio Change:{' '}
                        <span className={simulationResult.portfolio_delta.value_change >= 0 ? 'text-positive' : 'text-negative'}>
                          {simulationResult.portfolio_delta.value_change >= 0 ? '+' : ''}
                          ${simulationResult.portfolio_delta.value_change?.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {simulationResult.portfolio_delta?.income_change !== undefined && (
                      <div>
                        Monthly Income Change:{' '}
                        <span className={simulationResult.portfolio_delta.income_change >= 0 ? 'text-positive' : 'text-negative'}>
                          {simulationResult.portfolio_delta.income_change >= 0 ? '+' : ''}
                          ${simulationResult.portfolio_delta.income_change?.toFixed(2)}/mo
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                  <DocumentTextIcon className="w-4 h-4" />
                  Confirm the following:
                </h3>
                {checklist.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-start gap-3 p-3 bg-bone border border-hairline hover:bg-pine-tint/30 rounded-lg cursor-pointer transition-colors duration-300 group"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {item.checked ? (
                        <CheckCircleIconSolid className="w-5 h-5 text-pine" />
                      ) : (
                        <CheckCircleIcon className="w-5 h-5 text-faint group-hover:text-secondary" />
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => handleChecklistChange(item.id)}
                      className="hidden"
                    />
                    <span className={`text-sm ${item.checked ? 'text-primary' : 'text-secondary'}`}>
                      {item.label}
                    </span>
                  </label>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1 py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!allChecked}
                  className="btn-primary flex-1 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Execute Strategy
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MakeItRealButton;
